
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageSquare } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";

interface LatestComment {
  id: string;
  post_id: string;
  content: string;
  author_name: string;
  created_at: string;
  post_title?: string;
}

const LatestCommentsSection = () => {
  const [comments, setComments] = useState<LatestComment[]>([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchComments = async () => {
      try {
        const { data, error } = await supabase
          .from('post_comments')
          .select(`
            id,
            post_id,
            content,
            author_name,
            created_at,
            board_posts(title)
          `)
          .order('created_at', { ascending: false })
          .limit(3); // only 3 최신댓글
          
        if (error) throw error;
        
        const commentsWithPostTitles = data.map(comment => ({
          id: comment.id,
          post_id: comment.post_id,
          content: comment.content,
          author_name: comment.author_name,
          created_at: comment.created_at,
          post_title: comment.board_posts?.title
        }));
        
        setComments(commentsWithPostTitles);
      } catch (error) {
        console.error("Error fetching latest comments:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, []);
  
  const truncateContent = (content: string, maxLength = 30) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <div>
      <Card className="shadow-sm">
        <CardHeader className="pb-2 pt-3">
          <CardTitle className="text-base font-medium flex items-center gap-1.5">
            <MessageSquare size={16} className="text-primary" />
            최신댓글
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 py-2">
          {loading ? (
            <div className="text-center py-1.5 text-muted-foreground text-sm">불러오는 중...</div>
          ) : comments.length > 0 ? (
            <ul className="space-y-1.5">
              {comments.map((comment) => (
                <li key={comment.id} className="border-b pb-1.5 last:border-b-0 last:pb-0">
                  <Link 
                    to={`/board/post/${comment.post_id}`} 
                    className="block hover:text-primary transition-colors"
                  >
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {truncateContent(comment.content)}
                    </p>
                    {comment.post_title && (
                      <p className="text-xs text-primary mt-0.5">
                        {comment.post_title}
                      </p>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-1.5 text-muted-foreground text-sm">댓글이 없습니다</div>
          )}
        </CardContent>
      </Card>
      {/* 영화 포스터는 더 이상 여기서 보이지 않습니다 */}
    </div>
  );
};

export default LatestCommentsSection;
